<template>
  <property-report-card
    title="Historical Exposure"
    :description="explanation[metric.value]"
    :printing-mode="printingMode"
    :is-empty="filteredProperties.length === 0"
  >
    <template #actions>
      <q-btn v-if="!printingMode" id="historical-exposure_download" flat no-caps @click="exportCSV">
        <q-icon name="download" class="q-mr-sm" size="medium" />Download
      </q-btn>
      <q-select
        v-model="metric"
        transition-duration="100"
        :options="filteredMetricOptions"
        label="Metric"
        dense
        outlined
        style="width: 180px"
        @update:model-value="(val) => $tracker.changedFilter('metric', 'occupancy_trends', val)"
      />
    </template>
    <q-separator />
    <q-card-section>
      <property-compare-history-chart
        id="intercom-occupancy-content"
        class="chart"
        :label="metric.label"
        :history="history"
        :format-fnc="(val: number | null) => formatPercent(val, 1)"
        :default-legend="true"
        canvas-style="width: 100%; height: 100%"
      />
    </q-card-section>
  </property-report-card>
</template>

<script lang="ts">
import type { PropType } from 'vue'

import { type PropertyDetailsResponse, occupancyOverTime, type Occupancy } from 'common'
import PropertyCompareHistoryChart, {
  type Value
} from '@/components/PropertyReports/PropertyCompareHistoryChart.vue'
import { formatPercent } from '@/utils'
import PropertyReportCard from '@/components/PropertyReportCard.vue'

const colors = [
  '#F80',
  'rgba(255, 193, 7, 1)',
  'rgba(255, 235, 59, 1)',
  'rgba(255, 87, 34, 1)',
  'rgba(3, 169, 244, 1)',
  'rgba(233, 30, 99, 1)',
  'rgba(76, 175, 80, 1)',
  'rgba(156, 39, 176, 1)',
  'rgba(139, 195, 74, 1)',
  'rgba(0, 188, 212, 1)',
  'rgba(22, 191, 195, 1)',
  'rgba(40, 146, 220, 1)'
]

type MetricOption = 'leased_percent_advertised' | 'exposure' | 'release_exposure'

export default {
  name: 'PropertyCompareOccupancyChart',
  components: { PropertyReportCard, PropertyCompareHistoryChart },
  props: {
    subjectDetail: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    details: {
      type: Array<PropertyDetailsResponse>,
      required: true
    },
    dateRange: {
      type: Object as PropType<{ from: Date; to: Date }>,
      required: true
    },
    selectedBuildings: {
      type: Array as PropType<{ label: string; value: string }[]>,
      required: true
    },
    printingMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      formatPercent,
      metric: { label: 'Exposure', value: 'exposure' } as {
        label: string
        value: MetricOption
      },
      explanation: {
        leased_percent_advertised:
          'Track the evolution of advertised units, defined as the percentage of units that are not advertised as immediately available for any given day.',
        exposure:
          'Track the percentage of units listed for rent over time or by future move-in month.',
        release_exposure:
          'Track the percentage of units listed for rent over time or by future move-in month. Divide by the number of units that have been released for rent only.'
      },
      metricOptions: [
        { label: 'Leased % (Adv)', value: 'leased_percent_advertised' },
        { label: 'Exposure', value: 'exposure' },
        { label: 'Released Exposure', value: 'release_exposure' }
      ] as { label: string; value: MetricOption }[],
      selectedBuildingAverages: [] as { label: string; value: string }[]
    }
  },
  computed: {
    getterFn() {
      switch (this.metric.value) {
        case 'leased_percent_advertised':
          return (datapoint: Occupancy) => datapoint.advertisedOccupancy
        case 'exposure':
          return (datapoint: Occupancy) => datapoint.exposure
        case 'release_exposure':
          return (datapoint: Occupancy) => datapoint.exposure
        default:
          throw new Error('Unknown metric')
      }
    },
    allProperties() {
      return this.subjectDetail ? [this.subjectDetail].concat(this.details) : this.details
    },
    filteredProperties() {
      return this.allProperties.filter((prop) =>
        this.selectedBuildings.some((selected) => selected.value === prop.id)
      )
    },
    hasAnyLeaseUps() {
      return this.filteredProperties.some((prop) => prop.is_lease_up)
    },
    filteredMetricOptions() {
      return this.hasAnyLeaseUps
        ? this.metricOptions
        : this.metricOptions.filter((opt) => opt.value !== 'release_exposure')
    },
    history(): Value[] {
      // call occupancyOverTime for each property and print the results
      return this.filteredProperties.map((prop, idx) => {
        const history = occupancyOverTime(
          prop,
          this.dateRange.from,
          this.dateRange.to,
          this.metric.value === 'release_exposure'
        )

        return {
          label: prop.building_name || prop.street_address,
          history: history.flatMap((datapoint) => {
            const val = this.getterFn(datapoint)
            if (val === null) {
              return []
            }

            return {
              date: datapoint.asOf,
              value: val
            }
          }),
          color: colors[idx % colors.length]
        }
      })
    }
  },
  created() {
    if (this.subjectDetail) {
      this.selectedBuildingAverages = [
        {
          label: this.subjectDetail.building_name || this.subjectDetail.street_address,
          value: this.subjectDetail.id
        }
      ]
    }
  },
  methods: {
    exportCSV() {
      // export the data to a CSV file as | Property | Date | Leased % | Exposure |
      if (!this.history.length) {
        return
      }
      const exportDecimal = (num: number) => Math.round(num * 100) / 100
      const rows = this.allProperties.map((prop) => {
        const hist = occupancyOverTime(prop, this.dateRange.from, this.dateRange.to)
        return hist
          .map((datapoint) => {
            const leasedPercent = exportDecimal(datapoint.advertisedOccupancy ?? 0)
            const exposure = exportDecimal(datapoint.exposure ?? 0)
            return `${prop.building_name || prop.street_address},${datapoint.asOf},${leasedPercent},${exposure}`
          })
          .join('\n')
      })

      const today = new Date().toISOString().split('T')[0]
      const csv = `Property,Date,Leased %,Exposure\n${rows.join('\n')}`
      const filename = `hello-data-exposure-trend-${today}.csv`
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
      this.$tracker.exported('exposure_trend')
    }
  }
}
</script>
<style scoped>
.chart {
  height: 350px;
  width: 100%;
}
.text-positive {
  color: #4caf50;
}
.text-negative {
  color: #f44336;
}
</style>
